import { put, take, fork, select, call, race } from 'redux-saga/effects'
import { push } from 'connected-react-router'
import i18n from 'config/i18n'
import { handleRequest } from 'sagas/requests'
import { createGetRequestPagination } from 'state'
import { STATE_KEYS } from 'state/requests'
import {
    TeamActions,
    TeamListRequestPayload,
    CreateTeamRequestPayload,
    UpdateTeamRequestPayload,
    TeamState,
    saveTeam,
    saveTeamList,
    deleteTeamFromList,
    CreateMemberRequestPayload,
    UpdateMemberRequestPayload,
    BaseTeamBrandMemberPayload,
    MemberState,
    saveMember,
    saveTeamMemberList,
    deleteMemberFromTeamList,
    BaseListRequest,
    TeamsResponse,
    MembersResponse,
    BaseTeamPayload,
    saveTeamBrandList,
    CreateTeamBrandRequestPayload,
    UpdateTeamBrandRequestPayload,
    deleteBrandFromTeamList,
    saveRoles,
    UpdateRolePayload,
    updateRoleName,
    DeleteRolePayload,
    updateRoles,
    saveTeamPermissions,
    UpdateRoleActions,
    updateNewRole,
    AddRolePayload,
    GetRolesResponse,
    RoleResponse,
    PermissionEntitiesState,
    UpdateRolesPermissionsPayload,
    DeleteTeamPayload,
    UpdateTeamTypeRequestPayload,
    UpdateBusinessDetailsPayload,
    saveBusinessDetails,
    BaseBusinessDetails,
    resetBusinessDetails,
    UpdateOnboardingOptionsPayload,
    WalletState,
    saveTeamWallets,
    GetWalletTransactionsPayload,
    WalletTransaction,
    saveWalletTransactions,
} from 'state/teams'
import { PartipostApiPaginationWrapper } from 'services/api'
import {
    getTeams,
    getTeam,
    createTeam,
    updateTeam,
    deleteTeam,
    getTeamMembers,
    createMember,
    updateMember,
    deleteMember,
    getTeamBrands,
    resendMemberInvite,
    getBrand,
    createBrand,
    updateBrand,
    deleteBrand,
    getRoles,
    addRole,
    deleteRole,
    editRole,
    getPermissions,
    updateRolesPermissions,
    removeTeamMember,
    updateTeamType,
    updateBusinessDetails,
    getBusinessDetails,
    updateOnboardingOptions,
    getTeamWallets,
    getWalletTransactions,
} from 'services/api/teams'
import { showSnackBar } from 'services/toast'
import { BrandsResponse, CreateBrandResponse } from 'services/api/brands'
import { setTeamFilter } from 'state/filter'
import { getProfileDetail } from 'state/profile'
import { MAX_PAGE_SIZE } from 'state/review'

function* handleGetTeamsList(payload: TeamListRequestPayload): any {
    const { teamFilter } = payload
    yield handleRequest<BaseListRequest, TeamsResponse>({
        stateKey: STATE_KEYS.TeamsList,
        payload: { ...payload, limit: MAX_PAGE_SIZE },
        ApiHandler: getTeams,
        *onSuccess(response) {
            yield put(
                saveTeamList({
                    data: teamFilter ? [response?.data?.data] : response?.data?.data.results,
                }),
            )
        },
    })
}

export function* watchTeamsListRequestFlow(): any {
    while (true) {
        const action = yield take(TeamActions.TEAM_LIST_REQUEST)
        yield race({
            task: call(handleGetTeamsList, action.payload),
            cancelAction: take(TeamActions.CANCEL_TEAM_LIST),
        })
    }
}

function* handleGetTeam(payload: BaseTeamPayload): any {
    yield handleRequest<BaseTeamPayload, TeamState>({
        stateKey: STATE_KEYS.GetTeam,
        payload,
        ApiHandler: getTeam,
        *onSuccess(response) {
            if (response.data !== undefined) {
                yield put(saveTeam(response.data.data))
            }
        },
    })
}

export function* watchGetTeamRequestFlow(): any {
    while (true) {
        const action = yield take(TeamActions.GET_TEAM)
        yield fork(handleGetTeam, action.payload)
    }
}

function* handleCreateTeam(payload: CreateTeamRequestPayload): any {
    const { isInternalUser, name, service_fee_percentage, ambassador_enabled, type, is_ai_caption_review_enabled } =
        payload
    const apiPayload = {
        name,
        service_fee_percentage,
        type,
        ambassador_enabled,
        ...(is_ai_caption_review_enabled && { is_ai_caption_review_enabled }),
    }
    yield handleRequest<CreateTeamRequestPayload, TeamState>({
        stateKey: STATE_KEYS.CreateTeam,
        payload: apiPayload,
        ApiHandler: createTeam,
        *onSuccess(response) {
            if (response.data !== undefined) {
                yield put(saveTeam(response.data.data))
                if (!isInternalUser) {
                    yield put(setTeamFilter(response.data.data.uuid))
                }
                yield put(push(`/team/${response.data.data.uuid}/members`))
            }
            yield call(showSnackBar, {
                title: i18n.t('successMessage.createTeam'),
                type: 'positive',
            })
        },
        *onError(response) {
            yield call(showSnackBar, {
                title: i18n.t('errorMessage.general.teamNotSavedTitle'),
                type: 'error',
            })
        },
    })
}

export function* watchCreateTeamRequestFlow(): any {
    while (true) {
        const action = yield take(TeamActions.CREATE_TEAM)
        yield fork(handleCreateTeam, action.payload)
    }
}

export function* handleUpdateTeam(payload: UpdateTeamRequestPayload): any {
    yield handleRequest<UpdateTeamRequestPayload, TeamState>({
        stateKey: STATE_KEYS.UpdateTeam,
        payload,
        ApiHandler: updateTeam,
        *onSuccess(response) {
            if (response.data !== undefined) {
                yield put(saveTeam({ ...response.data.data, isUpdate: true }))
            }
            yield call(showSnackBar, {
                title: i18n.t('successMessage.editedTeamNameTitle'),
                type: 'positive',
            })
        },
        *onError(response) {
            yield call(showSnackBar, {
                title: i18n.t('errorMessage.general.teamNotUpdatedTitle'),
                type: 'error',
            })
        },
    })
}

export function* watchUpdateTeamRequestFlow(): any {
    while (true) {
        const action = yield take(TeamActions.UPDATE_TEAM)
        yield fork(handleUpdateTeam, action.payload)
    }
}

export function* handleUpdateTeamType(payload: UpdateTeamTypeRequestPayload): any {
    const { onSuccessCallBack, ...rest } = payload
    yield handleRequest<UpdateTeamTypeRequestPayload, TeamState>({
        stateKey: STATE_KEYS.UpdateTeamType,
        payload: rest,
        ApiHandler: updateTeamType,
        *onSuccess(response) {
            if (response.data !== undefined) {
                yield put(saveTeam({ ...response.data.data, isUpdate: true }))
            }
            yield call(showSnackBar, {
                title: i18n.t('successMessage.updatedTeamTypeSetting'),
                type: 'positive',
            })

            onSuccessCallBack?.()
        },
        *onError(response) {
            yield call(showSnackBar, {
                title: i18n.t('errorMessage.general.teamNotUpdatedTitle'),
                type: 'error',
            })
        },
    })
}

export function* watchUpdateTeamTypeRequestFlow(): any {
    while (true) {
        const action = yield take(TeamActions.UPDATE_TEAM_TYPE)
        yield fork(handleUpdateTeamType, action.payload)
    }
}

export function* handleDeleteTeam(payload: DeleteTeamPayload): any {
    yield handleRequest<DeleteTeamPayload, any>({
        stateKey: STATE_KEYS.DeleteTeam,
        payload,
        ApiHandler: deleteTeam,
        *onSuccess() {
            yield put(deleteTeamFromList(payload))
            yield put(getProfileDetail())
            yield call(showSnackBar, {
                title: i18n.t('successMessage.deletedTeamTitle'),
                type: 'positive',
            })
            yield put(push(payload.directPath))
            if (payload?.onSuccessCallBack) {
                payload.onSuccessCallBack(payload.teamUuid)
            }
        },
        *onError(response) {
            let title = i18n.t('errorMessage.validation.deletedTeamAlreadySubtitle')
            if (response?.general_errors?.reasons) {
                const error = response?.general_errors?.reasons[0] || ''
                if (error === 'USER_IS_INTERNAL') {
                    title = i18n.t('errorMessage.validation.cantDeleteInternalTeamSubtitle')
                }
                if (error === 'USER_IS_IN_ONE_TEAM') {
                    title = i18n.t('errorMessage.validation.cantDeleteInternalTeamSubtitle')
                }
            }
            yield call(showSnackBar, {
                title,
                type: 'error',
            })
        },
    })
}

export function* watchDeleteTeamRequestFlow(): any {
    while (true) {
        const action = yield take(TeamActions.DELETE_TEAM)
        yield fork(handleDeleteTeam, action.payload)
    }
}

function* handleCreateMember(payload: CreateMemberRequestPayload): any {
    const submitPayload = {
        teamUuid: payload.teamUuid,
        name: payload.name,
        email: payload.email,
        role: payload.role,
        business_type: payload.business_type,
    }
    const resetForm = payload.resetForm
    yield handleRequest<CreateMemberRequestPayload, MemberState>({
        stateKey: STATE_KEYS.CreateMember,
        payload: submitPayload,
        ApiHandler: createMember,
        *onSuccess(response) {
            if (response.data !== undefined) {
                yield put(saveMember({ teamUuid: submitPayload.teamUuid, ...response.data.data }))
            }
            yield call(showSnackBar, {
                title: i18n.t('successMessage.sentInviteEmail', { email: submitPayload.email }),
                type: 'positive',
            })
            if (resetForm) {
                yield call(resetForm)
            }
        },
        *onError(response) {
            const errorCode =
                response && response.validation_errors && response.validation_errors.email
                    ? response.validation_errors.email[0].code
                    : response.general_errors && response.general_errors.reasons && response.general_errors.reasons[0]

            let errorTitle = i18n.t('errorMessage.validation.cantSendInviteEmail', {
                email: submitPayload.email,
            })
            switch (errorCode) {
                case 'EMAIL_ALREADY_IN_USED_FOR_CM':
                    errorTitle = i18n.t('errorMessage.validation.userAlreadyInAnotherTeamSubtext')
                    break
                case 'EMAIL_ALREADY_IN_USED_FOR_APP':
                    errorTitle = i18n.t('errorMessage.validation.emailAddressAlreadyInAppSubtext')
                    break
                case 'INTERNAL_USER_CANNOT_BE_IN_MULTIPLE_TEAM':
                    errorTitle = i18n.t('errorMessage.validation.userAlreadyInAnotherTeamSubtext')
                    break
                case 'USER_IS_TEAM_MEMBER':
                    errorTitle = i18n.t('errorMessage.validation.userAlreadyInTeamSubtext')
                    break
            }

            yield call(showSnackBar, {
                title: errorTitle,
                type: 'error',
            })
        },
    })
}

export function* watchCreateMemberRequestFlow(): any {
    while (true) {
        const action = yield take(TeamActions.CREATE_MEMBER)
        yield fork(handleCreateMember, action.payload)
    }
}

function* handleUpdateMember(payload: UpdateMemberRequestPayload): any {
    yield handleRequest<UpdateMemberRequestPayload, MemberState>({
        stateKey: STATE_KEYS.UpdateMember,
        payload,
        ApiHandler: updateMember,
        *onSuccess(response) {
            if (response.data !== undefined) {
                yield put(saveMember({ teamUuid: payload.teamUuid, ...response.data.data }))
            }
            yield call(showSnackBar, {
                title: i18n.t('successMessage.editedMemberTitle'),
                type: 'positive',
            })
        },
        *onError(response) {
            yield call(showSnackBar, {
                title: i18n.t('errorMessage.general.cantEditMemberTitle'),
                type: 'error',
            })
        },
    })
}

export function* watchUpdateMemberRequestFlow(): any {
    while (true) {
        const action = yield take(TeamActions.UPDATE_MEMBER)
        yield fork(handleUpdateMember, action.payload)
    }
}

function* handleDeleteMember(payload: BaseTeamBrandMemberPayload): any {
    yield handleRequest<BaseTeamBrandMemberPayload, any>({
        stateKey: STATE_KEYS.DeleteMember,
        payload,
        ApiHandler: deleteMember,
        *onSuccess(response) {
            yield put(deleteMemberFromTeamList(payload))
            yield call(showSnackBar, {
                title: i18n.t('successMessage.deactivatedAccountTitle'),
                type: 'positive',
            })
        },
        *onError(response) {
            yield call(showSnackBar, {
                title: i18n.t('errorMessage.validation.cantDeactivateAccountSubtitle'),
                type: 'error',
            })
        },
    })
}

export function* watchDeleteMemberRequestFlow(): any {
    while (true) {
        const action = yield take(TeamActions.DELETE_MEMBER)
        yield fork(handleDeleteMember, action.payload)
    }
}

function* handleRemoveTeamMember(payload: BaseTeamBrandMemberPayload): any {
    yield handleRequest<BaseTeamBrandMemberPayload, any>({
        stateKey: STATE_KEYS.DeleteMember,
        payload,
        ApiHandler: removeTeamMember,
        *onSuccess(response) {
            yield put(deleteMemberFromTeamList(payload))
            yield call(showSnackBar, {
                title: i18n.t('successMessage.memberRemovedFromTeamTitle'),
                type: 'positive',
            })
        },
        *onError(response) {
            const errorCode =
                response && response.validation_errors && response.validation_errors.email
                    ? response.validation_errors.email[0].code
                    : response.general_errors && response.general_errors.reasons && response.general_errors.reasons[0]

            let errorTitle = i18n.t('errorMessage.general.cantRemoveMemberFromTeamTitle')

            switch (errorCode) {
                case 'USER_IS_IN_ONE_TEAM':
                    errorTitle = i18n.t('errorMessage.validation.memberShouldBeInTeamSubtitle')
                    break
                case 'USER_IS_INTERNAL':
                    errorTitle = i18n.t('errorMessage.businessLogic.cantRemoveInternalMemberSubtitle')
                    break
            }

            yield call(showSnackBar, {
                title: errorTitle,
                type: 'error',
            })
        },
    })
}

export function* watchRemoveTeamMemberRequestFlow(): any {
    while (true) {
        const action = yield take(TeamActions.REMOVE_TEAM_MEMBER)
        yield fork(handleRemoveTeamMember, action.payload)
    }
}

function* handleGetTeamMembersList(payload: TeamListRequestPayload): any {
    const { loadMore, teamUuid, loadAll } = payload
    const pagination = yield select(createGetRequestPagination(STATE_KEYS.TeamMembersList))
    const { hasMore, next } = pagination
    const requestPayload = loadMore && hasMore ? { url: next, teamUuid } : { ...payload, limit: 100 }
    if (!(loadMore && !hasMore)) {
        yield handleRequest<TeamListRequestPayload, MembersResponse>({
            stateKey: STATE_KEYS.TeamMembersList,
            payload: requestPayload,
            ApiHandler: getTeamMembers,
            *onSuccess(response) {
                yield put(
                    saveTeamMemberList({
                        data: response.data !== undefined ? response.data.data.results : undefined,
                        teamUuid,
                    }),
                )
                if (loadAll && response.data !== undefined && response.data.data.next) {
                    yield call(handleGetTeamMembersList, { ...payload, loadMore: true })
                }
            },
        })
    }
}

export function* watchTeamMembersListRequestFlow(): any {
    while (true) {
        const action = yield take(TeamActions.TEAM_MEMBER_LIST_REQUEST)
        yield fork(handleGetTeamMembersList, action.payload)
    }
}

function* handleResendMemberInvite(payload: BaseTeamBrandMemberPayload): any {
    yield handleRequest<BaseTeamBrandMemberPayload, any>({
        stateKey: STATE_KEYS.ResendMemberInvite,
        payload,
        ApiHandler: resendMemberInvite,
        *onSuccess(response) {
            yield call(showSnackBar, {
                title: i18n.t('successMessage.resentEmailInviteTitle'),
                type: 'positive',
            })
        },
        *onError(response) {
            yield call(showSnackBar, {
                title: i18n.t('errorMessage.general.emailInviteNotSentTitle'),
                type: 'error',
            })
        },
    })
}

export function* watchResendMemberInviteRequestFlow(): any {
    while (true) {
        const action = yield take(TeamActions.RESEND_MEMBER_INVITE)
        yield fork(handleResendMemberInvite, action.payload)
    }
}

function* handleGetTeamBrandsList(payload: TeamListRequestPayload): any {
    const { loadMore, teamUuid, loadAll, isInternalTeam } = payload
    const pagination = yield select(createGetRequestPagination(STATE_KEYS.TeamBrandsList))
    const { hasMore, next } = pagination
    const requestPayload = loadMore && hasMore ? { url: next, teamUuid } : { ...payload, limit: 100 }
    if (!(loadMore && !hasMore)) {
        if (isInternalTeam) {
            yield call(showSnackBar, {
                title: i18n.t('warningMessage.somethingWentWrongInternalTitle'),
                type: 'error',
            })
            return
        }
        yield handleRequest<TeamListRequestPayload, BrandsResponse>({
            stateKey: STATE_KEYS.TeamBrandsList,
            payload: requestPayload,
            ApiHandler: getTeamBrands,
            *onSuccess(response) {
                yield put(
                    saveTeamBrandList({
                        data: response.data !== undefined ? response.data.data.results : undefined,
                        teamUuid,
                    }),
                )
                if (loadAll && response.data !== undefined && response.data.data.next) {
                    yield call(handleGetTeamBrandsList, { ...payload, loadMore: true })
                }
            },
        })
    }
}

export function* watchTeamBrandsListRequestFlow(): any {
    while (true) {
        const action = yield take(TeamActions.TEAM_BRAND_LIST_REQUEST)
        yield fork(handleGetTeamBrandsList, action.payload)
    }
}

function* handleGetBrand(payload: BaseTeamBrandMemberPayload): any {
    yield handleRequest<BaseTeamBrandMemberPayload, CreateBrandResponse>({
        stateKey: STATE_KEYS.GetBrand,
        payload,
        ApiHandler: getBrand,
        *onSuccess(response) {
            if (response.data !== undefined) {
                yield put(
                    saveTeamBrandList({
                        data: [response.data.data],
                        teamUuid: payload.teamUuid,
                    }),
                )
            }
        },
    })
}

export function* watchGetBrandRequestFlow(): any {
    while (true) {
        const action = yield take(TeamActions.GET_BRAND)
        yield fork(handleGetBrand, action.payload)
    }
}

export function* handleCreateBrandRequest(payload: CreateTeamBrandRequestPayload): any {
    yield handleRequest<CreateTeamBrandRequestPayload, CreateBrandResponse>({
        stateKey: STATE_KEYS.CreateBrand,
        payload,
        ApiHandler: createBrand,
        *onSuccess(response) {
            yield call(showSnackBar, {
                title: i18n.t('successMessage.brandCreatedTitle'),
                type: 'positive',
            })
            if (response.data !== undefined) {
                yield put(
                    saveTeamBrandList({
                        data: [response.data.data],
                        teamUuid: payload.teamUuid,
                    }),
                )
            }
        },
    })
}

export function* watchCreateBrandRequestFlow(): any {
    while (true) {
        const action = yield take(TeamActions.CREATE_BRAND)
        yield fork(handleCreateBrandRequest, action.payload)
    }
}

export function* handleUpdateBrandRequest(payload: UpdateTeamBrandRequestPayload): any {
    yield handleRequest<UpdateTeamBrandRequestPayload, CreateBrandResponse>({
        stateKey: STATE_KEYS.UpdateBrand,
        payload,
        ApiHandler: updateBrand,
        *onSuccess(response) {
            if (response.data !== undefined) {
                yield put(
                    saveTeamBrandList({
                        data: [response.data.data],
                        teamUuid: payload.teamUuid,
                    }),
                )
                yield call(showSnackBar, {
                    title: i18n.t('successMessage.editBrandTitle'),
                    type: 'positive',
                })
            }
        },
    })
}

export function* watchUpdateBrandRequestFlow(): any {
    while (true) {
        const action = yield take(TeamActions.UPDATE_BRAND)
        yield fork(handleUpdateBrandRequest, action.payload)
    }
}

export function* handleDeleteBrandRequest(payload: BaseTeamBrandMemberPayload): any {
    yield handleRequest<BaseTeamBrandMemberPayload, any>({
        stateKey: STATE_KEYS.DeleteBrand,
        payload,
        ApiHandler: deleteBrand,
        *onSuccess(response) {
            yield put(deleteBrandFromTeamList({ teamUuid: payload.teamUuid, brandUuid: payload.brandUuid }))
            yield call(showSnackBar, {
                title: i18n.t('successMessage.brandDeletedTitle'),
                type: 'positive',
            })
        },
        *onError(response) {
            yield call(showSnackBar, {
                title: i18n.t('errorMessage.general.cantDeleteBrandTitle'),
                type: 'error',
            })
        },
    })
}

export function* watchDeleteBrandRequestFlow(): any {
    while (true) {
        const action = yield take(TeamActions.DELETE_BRAND)
        yield fork(handleDeleteBrandRequest, action.payload)
    }
}

function* handleGetRoles(payload: BaseTeamPayload): any {
    yield handleRequest<BaseTeamBrandMemberPayload, GetRolesResponse>({
        stateKey: STATE_KEYS.GetRoles,
        payload,
        ApiHandler: getRoles,
        *onSuccess(response) {
            if (response.data === undefined) return
            yield put(
                saveRoles({
                    roles: response.data.data.results,
                }),
            )
        },
    })
}

export function* watchGetRolesRequestFlow(): any {
    while (true) {
        const action = yield take(TeamActions.GET_ROLES)
        yield fork(handleGetRoles, action.payload)
    }
}

function* handleAddRole(payload: AddRolePayload): any {
    const { teamUuid, name, onSuccessCallback, onErrorCallback } = payload

    yield handleRequest<any, RoleResponse>({
        stateKey: STATE_KEYS.AddRole,
        payload: { teamUuid, name },
        ApiHandler: addRole,
        *onSuccess(response) {
            const { data } = response
            if (!data) return
            yield put(updateNewRole(data.data))
            if (onSuccessCallback) {
                onSuccessCallback()
            }
        },
        *onError() {
            if (onErrorCallback) {
                onErrorCallback()
            }
        },
    })
}

export function* watchAddRoleRequestFlow(): any {
    while (true) {
        const action = yield take(TeamActions.ADD_ROLE)
        yield fork(handleAddRole, action.payload)
    }
}

function* handleEditRoleName(payload: UpdateRolePayload): any {
    const { onSuccessCallback, onErrorCallback } = payload

    yield handleRequest<UpdateRolePayload, RoleResponse>({
        stateKey: STATE_KEYS.EditRole,
        payload,
        ApiHandler: editRole,
        *onSuccess(response) {
            const { data } = response
            if (!data || !data.data) return
            yield put(
                updateRoleName({
                    roleId: data.data.uuid,
                    roleName: data.data.name,
                }),
            )
            if (onSuccessCallback) {
                onSuccessCallback()
            }
        },
        *onError(response) {
            if (onErrorCallback) {
                onErrorCallback()
            }
            yield call(showSnackBar, {
                title: response.general_errors.reasons[0],
                type: 'error',
            })
        },
    })
}
export function* watchEditRoleNameRequestFlow(): any {
    while (true) {
        const action = yield take(TeamActions.EDIT_ROLE_NAME)
        yield fork(handleEditRoleName, action.payload)
    }
}
function* handleDeleteRole(payload: DeleteRolePayload): any {
    const { roleId } = payload
    yield handleRequest<any, Record<any, any>>({
        stateKey: STATE_KEYS.DeleteRole,
        payload,
        ApiHandler: deleteRole,
        *onSuccess() {
            yield put(updateRoles({ roleId, type: UpdateRoleActions.DELETE_ROLE }))
        },
        *onError(response) {
            yield call(showSnackBar, {
                title: response.general_errors.reasons[0],
                type: 'error',
            })
        },
    })
}
export function* watchDeleteRoleRequestFlow(): any {
    while (true) {
        const action = yield take(TeamActions.DELETE_ROLE)
        yield fork(handleDeleteRole, action.payload)
    }
}
function* handleGetTeamPermissions(payload: BaseTeamPayload): any {
    yield handleRequest<BaseTeamPayload, PermissionEntitiesState[]>({
        stateKey: STATE_KEYS.GetPermissions,
        payload,
        ApiHandler: getPermissions,
        *onSuccess(response) {
            if (!response.data || !response.data.data || !Array.isArray(response.data.data)) return
            yield put(
                saveTeamPermissions({
                    permissions: response.data.data,
                }),
            )
        },
    })
}
export function* watchGetTeamPermissionsRequestFlow(): any {
    while (true) {
        const action = yield take(TeamActions.GET_TEAM_PERMISSIONS)
        yield fork(handleGetTeamPermissions, action.payload)
    }
}
function* handleSubmitRolesPermissions(payload: UpdateRolesPermissionsPayload): any {
    const { refreshUserProfile, ...res } = payload
    yield handleRequest<any, Record<any, any>>({
        stateKey: STATE_KEYS.UpdateRolesPermissions,
        payload: res,
        ApiHandler: updateRolesPermissions,
        *onSuccess() {
            yield call(showSnackBar, {
                title: i18n.t('successMessage.saveNewPermissions'),
                type: 'positive',
            })
            yield put({ type: TeamActions.SYNC_ROLES })
            if (refreshUserProfile) {
                yield put(getProfileDetail({ stateKey: STATE_KEYS.UpdateRolesPermissions }))
            }
        },
        *onError(response) {
            if (response?.general_errors?.reasons[0] !== 'PERMISSION_DENIED') {
                yield call(showSnackBar, {
                    title: i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext'),
                    type: 'error',
                })
            }
        },
    })
}
export function* watchSubmitRolesPermissionsRequestFlow(): any {
    while (true) {
        const action = yield take(TeamActions.UPDATE_ROLES_PERMISSIONS)
        yield fork(handleSubmitRolesPermissions, action.payload)
    }
}

function* handleUpdateBusinessDetails(payload: UpdateBusinessDetailsPayload): any {
    yield handleRequest<UpdateBusinessDetailsPayload, BaseBusinessDetails>({
        stateKey: STATE_KEYS.UpdateBusinessDetails,
        payload: payload,
        ApiHandler: updateBusinessDetails,
        *onSuccess(response) {
            yield call(showSnackBar, {
                title: i18n.t('successMessage.billInfo.updateBillInfo'),
                type: 'positive',
            })
            if (response.data?.data) {
                yield put(saveBusinessDetails(response.data.data))
                yield put(getProfileDetail())
            }
        },
        *onError(response) {
            if (response?.general_errors?.reasons[0] !== 'PERMISSION_DENIED') {
                yield call(showSnackBar, {
                    title: i18n.t('errorMessage.campaignEditor.toast.cantTakeActionTitle'),
                    type: 'error',
                })
            }
        },
    })
}
export function* watchUpdateBusinessDetailsRequestFlow(): any {
    while (true) {
        const action = yield take(TeamActions.UPDATE_BUSINESS_DETAILS)
        yield fork(handleUpdateBusinessDetails, action.payload)
    }
}

function* handleGetBusinessDetails(payload: BaseTeamPayload): any {
    yield handleRequest<BaseTeamPayload, BaseBusinessDetails>({
        stateKey: STATE_KEYS.GetBusinessDetails,
        payload: payload,
        ApiHandler: getBusinessDetails,
        *onSuccess(response) {
            if (response.data?.data) {
                yield put(saveBusinessDetails(response.data.data))
            }
        },
        *onError(response) {
            if (
                response?.general_errors?.reasons[0] !== 'PERMISSION_DENIED' &&
                response?.general_errors?.reasons[0] !== 'NOT_FOUND'
            ) {
                yield call(showSnackBar, {
                    title: i18n.t('errorMessage.campaignEditor.toast.cantTakeActionTitle'),
                    type: 'error',
                })
            } else {
                yield put(resetBusinessDetails())
            }
        },
    })
}
export function* watchGetBusinessDetailsRequestFlow(): any {
    while (true) {
        const action = yield take(TeamActions.GET_BUSINESS_DETAILS)
        yield fork(handleGetBusinessDetails, action.payload)
    }
}

function* handleGetTeamWallets(payload: BaseTeamPayload): any {
    yield handleRequest<BaseTeamPayload, WalletState[]>({
        stateKey: STATE_KEYS.GetTeamWallets,
        payload,
        ApiHandler: getTeamWallets,
        *onSuccess(response) {
            const wallets = response?.data?.data
            yield put(saveTeamWallets({ teamUuid: payload.teamUuid!, wallets: Array.isArray(wallets) ? wallets : [] }))
        },
        *onError() {
            yield put(saveTeamWallets({ teamUuid: payload.teamUuid!, wallets: [] }))
        },
    })
}

export function* watchGetTeamWalletsFlow(): any {
    while (true) {
        const action = yield take(TeamActions.GET_TEAM_WALLETS)
        yield fork(handleGetTeamWallets, action.payload)
    }
}

function* handleGetWalletTransactions(payload: GetWalletTransactionsPayload): any {
    yield handleRequest<GetWalletTransactionsPayload, PartipostApiPaginationWrapper<WalletTransaction[]>>({
        stateKey: STATE_KEYS.GetWalletTransactions,
        payload,
        ApiHandler: getWalletTransactions,
        *onSuccess(response) {
            const data = response?.data?.data
            yield put(
                saveWalletTransactions({
                    walletUuid: payload.walletUuid,
                    results: Array.isArray(data?.results) ? data!.results : [],
                    count: data?.count ?? 0,
                }),
            )
        },
        *onError() {
            yield put(
                saveWalletTransactions({
                    walletUuid: payload.walletUuid,
                    results: [],
                    count: 0,
                }),
            )
        },
    })
}

export function* watchGetWalletTransactionsFlow(): any {
    while (true) {
        const action = yield take(TeamActions.GET_WALLET_TRANSACTIONS)
        yield fork(handleGetWalletTransactions, action.payload)
    }
}

function* handleUpdateOnboardingOptions(payload: UpdateOnboardingOptionsPayload): any {
    const { onSuccessCallback, ...rest } = payload
    yield handleRequest<UpdateOnboardingOptionsPayload, any>({
        stateKey: STATE_KEYS.UpdateOnboardingOptions,
        payload: rest,
        ApiHandler: updateOnboardingOptions,
        *onSuccess() {
            onSuccessCallback?.()
        },
        *onError(response) {
            if (response?.general_errors?.reasons?.[0] !== 'PERMISSION_DENIED') {
                yield call(showSnackBar, {
                    title: i18n.t('errorMessage.campaignEditor.toast.cantTakeActionTitle'),
                    type: 'error',
                })
            }
        },
    })
}
export function* watchUpdateOnboardingOptionsRequestFlow(): any {
    while (true) {
        const action = yield take(TeamActions.UPDATE_ONBOARDING_OPTIONS)
        yield fork(handleUpdateOnboardingOptions, action.payload)
    }
}
